import {
  ErrorOutlineOutlined as ErrorOutlineOutlinedIcon,
  ReportProblemOutlined as ReportProblemOutlinedIcon,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { RoutalPalette, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';

import { intl } from '../../../../helpers/IntGlobalProvider';
import InputLocation from '../../../../react-components/InputLocation';
import Text from '../../../../react-components/Text';

interface AddressReviewItemProps {
  clientLabel?: string;
  location?: Location & { original_input?: string };
  onAddressChange: (location?: Location | null) => void;
}

const AddressReviewItem = ({ clientLabel, location, onAddressChange }: AddressReviewItemProps) => (
  <Grid container direction="row" alignItems="center" wrap="nowrap">
    <Grid item container>
      {clientLabel ? (
        <Text variant="body" style={{ fontSize: `12px` }}>
          {clientLabel}
        </Text>
      ) : (
        <Text variant="body" style={{ color: RoutalPalette.secondary00, fontSize: `12px` }} intlId="no_name" />
      )}
    </Grid>

    <Grid item container>
      {location?.original_input ? (
        <Text variant="body" style={{ fontSize: `12px` }}>
          {location?.original_input}
        </Text>
      ) : (
        <Text variant="body" style={{ color: RoutalPalette.secondary00, fontSize: `12px` }} intlId="none" />
      )}
    </Grid>

    <Grid item container>
      <InputLocation
        fullWidth
        inputProps={{
          styles: {
            fontSize: `12px`,
          },
          size: `medium`,
          variant: `outlined`,
          placeholderIntlId: `location`,
        }}
        emptyIntlId="no_results"
        fieldValue={location}
        onUpdate={onAddressChange}
      />
    </Grid>

    <Grid item>
      <Grid container style={{ width: `50px` }} justifyContent="flex-end">
        {!location?.label ? (
          <RoutalTooltip title={intl.formatMessage({ id: `plans.dialogs.services_from_file.review.error` })}>
            <ErrorOutlineOutlinedIcon style={{ marginRight: `16px`, color: RoutalPalette.danger }} />
          </RoutalTooltip>
        ) : (
          <>
            {location.partial_match || !location.isValid() ? (
              <RoutalTooltip title={intl.formatMessage({ id: `plans.dialogs.services_from_file.review.alert` })}>
                <ReportProblemOutlinedIcon style={{ marginRight: `16px`, color: RoutalPalette.warning }} />
              </RoutalTooltip>
            ) : null}
          </>
        )}
      </Grid>
    </Grid>
  </Grid>
);

interface ReviewClientData {
  label?: string;
  location?: Location;
  location_details?: string;
  comments?: string;
  phone?: string;
  email?: string;
  url?: string;
  reference_person?: string;
  custom_fields?: Record<string, any>;
  default_duration?: number;
  default_requires?: string[];
  default_time_windows?: Array<[number, number]>;
  default_volume?: number;
  default_weight?: number;
}

interface ImportClientsDialogReviewProps {
  clients: ReviewClientData[];
  onAddressChange: ({ clientIndex, newAddress }: { clientIndex: number; newAddress?: Location }) => void;
}

const ImportClientsDialogReview = ({ clients, onAddressChange }: ImportClientsDialogReviewProps) => {
  const hasAlerts = () =>
    clients.some((client: ReviewClientData) => client.location?.partial_match || !client.location?.isValid());

  const sortClients = () => {
    return clients.sort((a: ReviewClientData, b: ReviewClientData) => {
      if (!a.location?.lat && !a.location?.lng) return -1;

      if (!b.location?.lat && !b.location?.lng) return 1;

      if (!a.location.isValid()) return -1;

      if (!b.location.isValid()) return 1;

      return ((a.location?.partial_match ?? false) > (b.location?.partial_match ?? false) ? 1 : -1) * -1;
    });
  };
  return (
    <Grid container direction="column">
      {hasAlerts() ? (
        <>
          <Grid item container justifyContent="center">
            <Text
              variant="body"
              style={{ fontSize: `12px`, color: RoutalPalette.danger }}
              intlId="plans.dialogs.services_from_file.review.has_alerts"
            />
          </Grid>
          <VSpacer medium />
        </>
      ) : null}
      <Grid container direction="row" wrap="nowrap">
        <Grid item container>
          <Text variant="label" intlId="plans.dialogs.services_from_file.review.label" />
        </Grid>

        <Grid item container>
          <Text variant="label" intlId="plans.dialogs.services_from_file.review.input_location" />
        </Grid>

        <Grid item container>
          <Text variant="label" intlId="plans.dialogs.services_from_file.review.detected_location" />
        </Grid>

        <Grid item container style={{ width: `50px` }}>
          <Text variant="label" intlId="plans.dialogs.services_from_file.review.alerts" />
        </Grid>
      </Grid>

      <VSpacer medium />

      {sortClients().map((client, idx) => (
        <Grid container direction="column" key={`client-review-${idx}`}>
          <AddressReviewItem
            clientLabel={client.label}
            location={client.location}
            onAddressChange={(location?: Location | null) =>
              onAddressChange({ clientIndex: idx, newAddress: location ?? undefined })
            }
          />
          <VSpacer small />
        </Grid>
      ))}
    </Grid>
  );
};

export default ImportClientsDialogReview;
